import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { useFeatures } from "@/lib/features";
import { useBCM } from "@/lib/bcm";
import {
  LayoutDashboard, Users, Network, CalendarCheck, CalendarRange, CheckSquare,
  ListTodo, FolderKanban, Inbox as InboxIcon, UserPlus, LogOut as LogOutIcon,
  Laptop, FileText, Megaphone, MessageSquare, Sparkles, Settings as SettingsIcon,
  Search, Bell, ChevronDown, Eye, Building2, CalendarDays, Settings2,
  Wallet, Video, Menu, LifeBuoy, Receipt, Target, Briefcase, Activity,
  CheckCheck, ChevronRight, Archive, AlertCircle, Ticket, ClipboardList,
  CalendarClock, Lock, CreditCard, Clock4, Database, Workflow, GraduationCap,
  Bot, Hash,
} from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent,
  DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import FloatingChat from "./FloatingChat";
import CreateMenu from "./CreateMenu";
import MobileBottomNav from "./MobileBottomNav";

const NAV = [
  // === OVERVIEW ===
  { to: "/", label: "Dashboard", icon: LayoutDashboard, end: true, group: "overview", flag: "dashboard" },
  { to: "/inbox", label: "Inbox", icon: InboxIcon, group: "overview", flag: "inbox" },
  { to: "/ai", label: "AI Assistant", icon: Sparkles, group: "overview", accent: true, flag: "ai" },

  // === WORKFORCE ===
  { to: "/people", label: "Employees", icon: Users, group: "workforce", flag: "people", managerOnly: true },
  { to: "/org-chart", label: "Organization", icon: Network, group: "workforce", flag: "org_chart" },
  { to: "/attendance", label: "My Attendance", icon: CalendarCheck, group: "workforce", flag: "attendance", relabelEmp: "My Attendance" },
  { to: "/leaves", label: "Leave & Absence", icon: CalendarRange, group: "workforce", flag: "leaves", relabelEmp: "My Leave" },
  { to: "/approvals", label: "Approvals", icon: CheckSquare, group: "workforce", flag: "approvals", managerOnly: true },
  { to: "/shifts", label: "Shifts", icon: CalendarClock, group: "workforce", flag: "shifts", adminOnly: true },
  { to: "/timesheets", label: "Timesheets", icon: Clock4, group: "workforce", flag: "timesheets", relabelEmp: "My Timesheet" },
  { to: "/timesheet-approvals", label: "TS Approvals", icon: Clock4, group: "workforce", flag: "timesheets", managerOnly: true },
  { to: "/performance", label: "Performance", icon: Target, group: "workforce", flag: "performance" },

  // === TALENT ===
  { to: "/recruitment", label: "Recruitment", icon: Briefcase, group: "talent", flag: "recruitment", adminOnly: true },
  { to: "/recruitment-ats", label: "Recruitment Pro", icon: Briefcase, group: "talent", adminOnly: true },
  { to: "/onboarding", label: "Onboarding", icon: UserPlus, group: "talent", flag: "onboarding", adminOnly: true },
  { to: "/offboarding", label: "Offboarding", icon: LogOutIcon, group: "talent", flag: "offboarding", adminOnly: true },
  { to: "/lms", label: "Learning", icon: GraduationCap, group: "talent" },
  { to: "/performance-pro", label: "Performance Pro", icon: Target, group: "talent", managerOnly: true },

  // === PAYROLL & COMPS ===
  { to: "/payroll", label: "Payroll", icon: Wallet, group: "payroll_comps", flag: "payroll", relabelEmp: "My Payslips" },
  { to: "/claims", label: "Expenses & Claims", icon: Receipt, group: "payroll_comps", flag: "claims" },
  { to: "/assets", label: "Assets", icon: Laptop, group: "payroll_comps", flag: "assets", relabelEmp: "My Assets" },

  // === WORK & COLLAB ===
  { to: "/tasks", label: "My Tasks", icon: ListTodo, group: "work", flag: "tasks" },
  { to: "/projects", label: "Projects", icon: FolderKanban, group: "work", flag: "projects", relabelEmp: "My Projects" },
  { to: "/meetings", label: "Meetings", icon: Video, group: "work", flag: "meetings", relabelEmp: "My Meetings" },
  { to: "/calendar", label: "Calendar", icon: CalendarDays, group: "work", flag: "calendar" },
  { to: "/communication", label: "Communication", icon: Hash, group: "work", managerOnly: true },
  { to: "/meetings-video", label: "Video Calls", icon: Video, group: "work" },
  { to: "/messaging", label: "Messaging", icon: MessageSquare, group: "work", flag: "messaging" },
  { to: "/announcements", label: "Announcements", icon: Megaphone, group: "work", flag: "announcements" },
  { to: "/helpdesk", label: "Helpdesk", icon: LifeBuoy, group: "work", flag: "helpdesk" },

  // === ANALYTICS & REPORTS ===
  { to: "/documents", label: "Documents", icon: FileText, group: "analytics", flag: "documents", relabelEmp: "My Documents" },
  { to: "/audit-log", label: "Reports", icon: Activity, group: "analytics", flag: "audit_log", adminOnly: true },
  { to: "/ai-copilots", label: "AI Copilots", icon: Bot, group: "analytics", accent: true, managerOnly: true },
  { to: "/workflow-studio", label: "Workflow Studio", icon: Workflow, group: "analytics", adminOnly: true },

  // === ADMIN ===
  { to: "/organization", label: "Departments", icon: Building2, group: "admin", flag: "organization", adminOnly: true },
  { to: "/holidays", label: "Holidays", icon: CalendarDays, group: "admin", flag: "holidays", adminOnly: true },
  { to: "/notifications", label: "Notifications", icon: Bell, group: "admin" },
  { to: "/feature-controls", label: "Feature Controls", icon: Settings2, group: "admin", flag: "feature_controls", superOnly: true },
  { to: "/backup-restore", label: "Backup & Restore", icon: Database, group: "admin", superOnly: true },
  { to: "/subscription", label: "Subscription", icon: CreditCard, group: "admin", superOnly: true },
  { to: "/settings", label: "Settings", icon: SettingsIcon, group: "admin", superOnly: true },

  // PLATFORM OWNER ONLY — switch button to Platform Console (handled separately, NOT a regular nav).
  // Hidden for tenant users.
];

const GROUPS = {
  overview:      { label: "Overview",            dot: "bg-violet-500" },
  workforce:     { label: "Workforce",           dot: "bg-blue-500" },
  talent:        { label: "Talent",              dot: "bg-emerald-500" },
  payroll_comps: { label: "Payroll & Comps",     dot: "bg-amber-500" },
  work:          { label: "Work & Collab",       dot: "bg-indigo-500" },
  analytics:     { label: "Analytics & Reports", dot: "bg-fuchsia-500" },
  admin:         { label: "Admin",               dot: "bg-slate-500" },
};

export default function Layout() {
  const { user, settings, logout } = useAuth();
  const { isModuleOn, flags } = useFeatures();
  const { isGroupEnabled } = useBCM();
  const nav = useNavigate();
  const [inboxCount, setInboxCount] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsedGroups, setCollapsedGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem("hreye_collapsed_groups") || "{}"); } catch { return {}; }
  });
  const toggleGroup = (g) => {
    setCollapsedGroups(prev => {
      const next = { ...prev, [g]: !prev[g] };
      try { localStorage.setItem("hreye_collapsed_groups", JSON.stringify(next)); } catch {}
      return next;
    });
  };
  const [changePwOpen, setChangePwOpen] = useState(false);
  const [cpCurrent, setCpCurrent] = useState("");
  const [cpNew, setCpNew] = useState("");
  const [cpConfirm, setCpConfirm] = useState("");
  const [cpBusy, setCpBusy] = useState(false);

  const submitChangePw = async () => {
    if (cpNew.length < 8) return toast.error("Password must be at least 8 characters");
    if (cpNew !== cpConfirm) return toast.error("New passwords don't match");
    setCpBusy(true);
    try {
      await api.post("/auth/change-password", { current_password: cpCurrent, new_password: cpNew });
      toast.success("Password changed");
      setChangePwOpen(false); setCpCurrent(""); setCpNew(""); setCpConfirm("");
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to change password"); }
    finally { setCpBusy(false); }
  };

  useEffect(() => {
    api.get("/inbox").then((r) => {
      setInboxCount(r.data.filter((n) => !n.read).length);
    }).catch(() => {});
  }, []);

  const canSee = (n) => {
    if (n.flag && !isModuleOn(n.flag)) return false;
    // Role-visibility override (super admin configurable)
    const rv = flags?.role_visibility;
    if (n.flag && rv && rv[n.flag]) {
      const allowed = rv[n.flag];
      if (Array.isArray(allowed) && !allowed.includes(user?.role)) return false;
    }
    if (n.platformOwnerOnly && !user?.is_platform_owner) return false;
    if (n.superOnly && user?.role !== "super_admin") return false;
    if (n.adminOnly && !["super_admin", "hr_admin"].includes(user?.role)) return false;
    if (n.managerOnly && !["super_admin", "hr_admin", "manager", "department_head", "team_lead"].includes(user?.role)) return false;
    return true;
  };

  const visibleNav = NAV.filter(canSee).map(n => {
    if (user?.role === "employee" && n.relabelEmp) {
      return { ...n, label: n.relabelEmp };
    }
    return n;
  });
  const groups = {};
  visibleNav.forEach((n) => { (groups[n.group] ||= []).push(n); });

  return (
    <div className="min-h-screen flex bg-slate-50" data-testid="app-layout">
      {/* Sidebar (desktop + mobile drawer) */}
      <aside
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 fixed lg:sticky top-0 z-40 w-[260px] h-screen bg-white border-r border-slate-200 text-slate-700 flex flex-col shrink-0 transition-transform duration-200`}
        data-testid="sidebar"
      >
        <Link to="/" className="flex items-center gap-3 px-5 py-4 border-b border-slate-200" onClick={() => setMobileOpen(false)}>
          <div className="w-11 h-11 rounded-xl overflow-hidden bg-slate-50 ring-1 ring-slate-200 shrink-0 flex items-center justify-center shadow-sm">
            <img
              src="/qrewhr-logo.jpg"
              alt="Qrew HR"
              className="w-full h-full object-cover"
              onError={(e) => { e.currentTarget.style.display = "none"; }}
            />
          </div>
          <div className="min-w-0 flex-1">
            <div className="font-bold font-[Outfit] text-[16px] leading-tight tracking-tight text-slate-900 truncate" data-testid="app-name">Qrew HR</div>
            <div className="text-[10px] text-slate-500 truncate mt-0.5">Smart HR. Stronger Teams.</div>
          </div>
        </Link>

        <nav className="flex-1 overflow-y-auto px-3 py-3 pb-24 lg:pb-3 no-scrollbar">
          {Object.entries(groups).filter(([gk]) => isGroupEnabled(gk)).map(([gk, items]) => {
            const g = GROUPS[gk] || { label: gk, dot: "bg-slate-400" };
            const collapsed = !!collapsedGroups[gk];
            return (
              <div key={gk} className="mb-3" data-testid={`nav-group-${gk}`}>
                <button
                  onClick={() => toggleGroup(gk)}
                  className="w-full flex items-center gap-2 px-3 py-1 text-[10px] uppercase tracking-[0.18em] font-bold text-slate-400 hover:text-slate-600 transition"
                  data-testid={`nav-group-toggle-${gk}`}
                >
                  <span className={`w-1.5 h-1.5 rounded-full ${g.dot}`} />
                  <span className="flex-1 text-left">{g.label}</span>
                  <ChevronDown className={`w-3 h-3 transition-transform ${collapsed ? "-rotate-90" : ""}`} />
                </button>
                <div className={`mt-1 space-y-0.5 overflow-hidden transition-all ${collapsed ? "max-h-0 opacity-0" : "max-h-[600px] opacity-100"}`}>
                  {items.map((n) => (
                    <NavLink
                      key={n.to}
                      to={n.to}
                      end={n.end}
                      onClick={() => setMobileOpen(false)}
                      data-testid={`nav-${n.to === "/" ? "dashboard" : n.to.replace("/", "")}`}
                      className={({ isActive }) =>
                        `relative flex items-center gap-3 px-3 h-[42px] text-[13px] rounded-lg transition-all duration-150 ${
                          isActive
                            ? "bg-gradient-to-r from-violet-100 to-fuchsia-50 text-violet-800 font-semibold ring-1 ring-violet-200 before:content-[''] before:absolute before:left-0 before:top-2 before:bottom-2 before:w-[3px] before:rounded-r-full before:bg-violet-500"
                            : "text-slate-600 hover:bg-slate-50 hover:text-slate-900"
                        }`
                      }
                    >
                      <n.icon className={`w-[18px] h-[18px] shrink-0 ${n.accent ? "text-fuchsia-500" : ""}`} />
                      <span className="flex-1 truncate">{n.label}</span>
                      {n.accent && <span className="w-1.5 h-1.5 rounded-full bg-fuchsia-500" />}
                    </NavLink>
                  ))}
                </div>
              </div>
            );
          })}
        </nav>

        <div className="px-4 py-3 border-t border-slate-200 flex items-center gap-2 text-[10px] text-slate-500">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>v1.9 · All systems operational</span>
        </div>
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setMobileOpen(false)} />
      )}

      {/* Main area */}
      <div className="flex-1 flex flex-col min-w-0">
        <ImpersonationBanner />
        <SeatLimitListener />
        <FocusModeBar />
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 lg:px-8 gap-3 sticky top-0 z-20" data-testid="top-header">
          <button
            className="lg:hidden p-2 -ml-1 text-slate-600 hover:bg-slate-100 rounded-md"
            onClick={() => setMobileOpen(true)}
            data-testid="mobile-menu-toggle"
            aria-label="Open navigation"
          >
            <Menu className="w-5 h-5" />
          </button>

          {/* Full-width flexible search */}
          <div className="relative flex-1 min-w-0 hidden sm:block">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none" />
            <Input
              placeholder="Search employees, tasks, documents, tickets…"
              className="pl-10 pr-4 h-10 w-full bg-slate-50 border-slate-200 rounded-lg focus-visible:ring-2 focus-visible:ring-cyan-500/40 focus-visible:border-cyan-400 placeholder:text-slate-400 text-sm"
              data-testid="global-search"
            />
            <kbd className="hidden lg:flex absolute right-3 top-1/2 -translate-y-1/2 items-center gap-1 px-1.5 py-0.5 rounded border border-slate-200 bg-white text-[10px] font-medium text-slate-400">⌘K</kbd>
          </div>
          <div className="flex-1 sm:hidden" />

          {/* Action cluster — right side */}
          <div className="flex items-center gap-2 shrink-0">
            <CreateMenu />

            <button
              onClick={() => nav("/ai")}
              className="hidden md:flex items-center gap-1.5 h-10 px-3.5 text-sm font-medium rounded-lg border border-cyan-500/40 bg-cyan-500/5 text-cyan-700 hover:bg-cyan-500/10 hover:border-cyan-500 transition-all ai-glow"
              data-testid="header-ai-btn"
            >
              <Sparkles className="w-4 h-4" />
              <span className="hidden lg:inline">AI Assistant</span>
              <span className="lg:hidden">AI</span>
            </button>

            {/* Notification bell with dropdown preview */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="relative w-10 h-10 flex items-center justify-center rounded-lg hover:bg-slate-100 transition" data-testid="inbox-bell" aria-label="Notifications">
                  <Bell className="w-5 h-5 text-slate-600" />
                  {inboxCount > 0 && (
                    <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] px-1 rounded-full bg-gradient-to-br from-rose-500 to-red-600 text-white text-[10px] font-semibold flex items-center justify-center border-2 border-white shadow">
                      {inboxCount > 99 ? "99+" : inboxCount}
                    </span>
                  )}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-[360px] p-0 overflow-hidden" data-testid="bell-dropdown">
                <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between bg-gradient-to-r from-slate-50 to-white">
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.2em] font-semibold text-slate-500">Notifications</div>
                    <div className="text-sm font-semibold text-slate-900 mt-0.5">{inboxCount} unread</div>
                  </div>
                  <Button size="sm" variant="ghost" className="h-7 text-[11px]" onClick={async () => {
                    try { await api.post("/inbox/mark-all-read"); toast.success("All marked as read"); setInboxCount(0); }
                    catch { toast.error("Failed"); }
                  }} data-testid="bell-mark-all"><CheckCheck className="w-3.5 h-3.5 mr-1"/>Mark all</Button>
                </div>
                <BellPreviewList onOpen={() => nav("/inbox")} />
                <div className="border-t border-slate-200 p-2">
                  <Button variant="ghost" size="sm" className="w-full h-8 text-xs justify-center hover:bg-slate-50" onClick={() => nav("/inbox")} data-testid="bell-view-all">
                    View all notifications <ChevronRight className="w-3.5 h-3.5 ml-1"/>
                  </Button>
                </div>
              </DropdownMenuContent>
            </DropdownMenu>

            <div className="w-px h-7 bg-slate-200 mx-1 hidden sm:block" />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button className="flex items-center gap-2.5 h-10 pl-1 pr-2 sm:pr-3 rounded-lg hover:bg-slate-100 transition" data-testid="user-menu">
                  <Avatar className="w-8 h-8 ring-2 ring-white shadow-sm">
                    {user?.avatar_url && <AvatarImage src={user.avatar_url} alt={user.full_name}/>}
                    <AvatarFallback className="bg-gradient-to-br from-slate-800 to-slate-900 text-white text-xs font-semibold">
                      {(user?.full_name || "U").split(" ").map(s => s[0]).slice(0,2).join("")}
                    </AvatarFallback>
                  </Avatar>
                  <div className="text-left leading-tight hidden md:block max-w-[140px]">
                    <div className="text-sm font-semibold text-slate-900 truncate">{user?.full_name}</div>
                    <div className="text-[11px] text-slate-500 capitalize truncate">{user?.role?.replace("_", " ")}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400 hidden md:block" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>Signed in</DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={() => nav(`/people/${user?.employee_id}`)} data-testid="menu-my-profile">
                  My Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setChangePwOpen(true)} data-testid="menu-change-password">
                  Change password
                </DropdownMenuItem>
                {user?.role === "super_admin" && <DropdownMenuItem onClick={() => nav("/settings")}>Settings</DropdownMenuItem>}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto pb-20 lg:pb-0">
          <Outlet />
        </main>
        <FloatingChat />
        <MobileBottomNav onOpenSidebar={() => setMobileOpen(true)} />
      </div>

      {/* Change Password dialog */}
      <Dialog open={changePwOpen} onOpenChange={setChangePwOpen}>
        <DialogContent className="max-w-md" data-testid="change-password-dialog">
          <DialogHeader>
            <DialogTitle>Change password</DialogTitle>
            <DialogDescription>Enter your current password and pick a new one (minimum 8 characters).</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div><Label>Current password</Label><input type="password" value={cpCurrent} onChange={(e) => setCpCurrent(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="cp-current" /></div>
            <div><Label>New password</Label><input type="password" value={cpNew} onChange={(e) => setCpNew(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="cp-new" /></div>
            <div><Label>Confirm new password</Label><input type="password" value={cpConfirm} onChange={(e) => setCpConfirm(e.target.value)} className="mt-1.5 w-full h-10 px-3 rounded-md border border-slate-200 text-sm" data-testid="cp-confirm" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setChangePwOpen(false)}>Cancel</Button>
            <Button className="bg-slate-900 hover:bg-slate-800" onClick={submitChangePw} disabled={cpBusy || !cpCurrent || !cpNew} data-testid="cp-submit">{cpBusy ? "Updating…" : "Update password"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


/* ==================== BELL DROPDOWN PREVIEW ==================== */
const BELL_EVENT_LABELS = {
  "leave.requested": "Leave approval needed", "leave.approved": "Leave approved", "leave.rejected": "Leave declined",
  "task.created": "New task", "task.assigned": "Task assigned to you", "task.reassigned": "Task reassigned",
  "task.status_changed": "Task status updated", "task.commented": "New comment on task",
  "task.due_date_changed": "Task due date changed", "task.follower_added": "Watching new task",
  "task.completed": "Task completed", "task.overdue": "Task overdue", "task.mentioned": "Mentioned in task",
  "task.due_today": "Task due today",
  "meeting.invited": "Meeting invite", "meeting.reminder": "Meeting soon", "meeting.cancelled": "Meeting cancelled",
  "ticket.created": "New ticket", "ticket.assigned": "Ticket assigned to you", "ticket.updated": "Ticket update",
  "claim.submitted": "Claim awaits approval", "claim.approved": "Claim approved", "claim.rejected": "Claim declined",
  "announcement.published": "Announcement", "payroll.payslip_ready": "Payslip available",
  "document.shared": "Document shared", "recruitment.offer": "Offer sent",
  "goal.updated": "Goal updated",
  "lifecycle.onboarding_start": "Onboarding started", "lifecycle.offboarding_start": "Offboarding started",
};

function bellIcon(type) {
  if (type.startsWith("task.")) return ClipboardList;
  if (type.startsWith("meeting.")) return CalendarClock;
  if (type.startsWith("ticket.") || type.startsWith("claim.")) return Ticket;
  if (type.startsWith("announcement.")) return Megaphone;
  if (type.startsWith("payroll.") || type.startsWith("document.")) return Wallet;
  if (type.startsWith("leave.")) return CheckSquare;
  return Bell;
}

function timeAgoShort(iso) {
  if (!iso) return "";
  const s = Math.floor((Date.now() - new Date(iso)) / 1000);
  if (s < 60) return "now";
  if (s < 3600) return `${Math.floor(s/60)}m`;
  if (s < 86400) return `${Math.floor(s/3600)}h`;
  return `${Math.floor(s/86400)}d`;
}

function BellPreviewList({ onOpen }) {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/inbox", { params: { limit: 8 } });
        setItems(r.data);
      } catch {}
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <div className="p-6 text-center text-xs text-slate-400">Loading…</div>;
  if (!items.length) return (
    <div className="p-8 text-center">
      <CheckCheck className="w-8 h-8 text-emerald-400 mx-auto"/>
      <div className="mt-2 text-sm font-medium text-slate-700">You're all caught up</div>
      <div className="text-[11px] text-slate-400 mt-0.5">New notifications will appear here</div>
    </div>
  );

  return (
    <div className="max-h-[420px] overflow-y-auto">
      {items.map(n => {
        const Icon = bellIcon(n.type || "");
        const label = BELL_EVENT_LABELS[n.type] || (n.type || "").replace(/_/g, " ");
        const important = ["leave.requested", "claim.submitted", "task.assigned", "task.reassigned", "ticket.assigned"].includes(n.type);
        return (
          <button
            key={n.id}
            onClick={() => { nav(n.link || "/inbox"); api.post(`/inbox/${n.id}/read`).catch(()=>{}); }}
            className={`w-full text-left px-4 py-2.5 flex items-start gap-3 hover:bg-slate-50 transition border-b border-slate-100 last:border-0 ${!n.read ? "bg-cyan-50/30" : ""}`}
            data-testid={`bell-item-${n.id}`}
          >
            <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${important ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-600"}`}>
              <Icon className="w-4 h-4"/>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className={`text-[10px] uppercase tracking-wider font-semibold ${important ? "text-amber-700" : "text-slate-500"}`}>{label}</span>
                {!n.read && <span className="w-1.5 h-1.5 rounded-full bg-cyan-500"/>}
                <span className="ml-auto text-[10px] text-slate-400">{timeAgoShort(n.created_at)}</span>
              </div>
              <div className="text-sm text-slate-900 mt-0.5 truncate">{n.title || "Notification"}</div>
              {n.body && <div className="text-[11px] text-slate-500 mt-0.5 truncate">{n.body}</div>}
            </div>
          </button>
        );
      })}
    </div>
  );
}

/* ==================== IMPERSONATION BANNER ==================== */
function ImpersonationBanner() {
  const { user } = useAuth();
  if (!user?.impersonating && !user?.original_user_id) return null;
  const exit = async () => {
    try {
      const { data } = await api.post("/master/exit-impersonation");
      localStorage.setItem("hreye_token", data.token);
      localStorage.setItem("hreye_user", JSON.stringify(data.user));
      window.location.href = "/master";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to exit impersonation");
    }
  };
  return (
    <div className="h-10 bg-gradient-to-r from-amber-400 to-orange-500 text-white px-4 flex items-center justify-between text-sm font-semibold shadow-sm" data-testid="impersonation-banner">
      <div className="flex items-center gap-2">
        <Eye className="w-4 h-4" />
        Impersonating · {user.email} · role {user.role}
      </div>
      <button onClick={exit} className="px-3 h-7 rounded-md bg-white/20 hover:bg-white/30 text-xs" data-testid="exit-impersonation">Exit impersonation</button>
    </div>
  );
}

/* ==================== SEAT LIMIT LISTENER ==================== */
function SeatLimitListener() {
  const [info, setInfo] = useState(null);
  useEffect(() => {
    const h = (e) => setInfo(e.detail);
    window.addEventListener("hreye:seat-limit-reached", h);
    return () => window.removeEventListener("hreye:seat-limit-reached", h);
  }, []);
  if (!info) return null;
  return (
    <Dialog open={!!info} onOpenChange={(v) => !v && setInfo(null)}>
      <DialogContent className="max-w-md" data-testid="seat-limit-dialog">
        <DialogHeader>
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-amber-400 to-orange-600 text-white flex items-center justify-center mx-auto">
            <Lock className="w-6 h-6" />
          </div>
          <DialogTitle className="text-center mt-3">Seat limit reached</DialogTitle>
          <DialogDescription className="text-center">
            Your <span className="font-semibold capitalize text-slate-900">{info.plan}</span> plan includes <span className="font-semibold text-slate-900">{info.seat_limit}</span> seats.
            You currently have <span className="font-semibold text-slate-900">{info.current_seats}</span> people. To add more team members, upgrade your plan.
          </DialogDescription>
        </DialogHeader>
        <div className="mt-2 p-4 rounded-lg bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200">
          <div className="text-xs uppercase tracking-wider font-semibold text-amber-700">Next step</div>
          <div className="text-sm text-slate-700 mt-1">Contact your platform admin, or upgrade via <span className="font-semibold">Settings → Billing</span> when Stripe is live (Round 13).</div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => setInfo(null)}>Dismiss</Button>
          <Button className="bg-gradient-to-r from-amber-500 to-orange-600 hover:from-amber-600 hover:to-orange-700 text-white" onClick={() => setInfo(null)} data-testid="seat-limit-upgrade">Upgrade plan</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ==================== FOCUS MODE TIMER BAR ==================== */
function FocusModeBar() {
  const [active, setActive] = useState(null);     // {task_id, title, started_at, entry_id}
  const [elapsed, setElapsed] = useState(0);
  const nav = useNavigate();

  // Poll for any running timer every 30s + on CustomEvent from Tasks page
  useEffect(() => {
    let abort = false;
    const poll = async () => {
      try {
        const { data } = await api.get("/me/active-timer");
        if (!abort) setActive(data && data.active ? data.active : null);
      } catch { if (!abort) setActive(null); }
    };
    poll();
    const id = setInterval(poll, 30000);
    const h = () => poll();
    window.addEventListener("hreye:timer-changed", h);
    return () => { abort = true; clearInterval(id); window.removeEventListener("hreye:timer-changed", h); };
  }, []);

  useEffect(() => {
    if (!active?.started_at) { setElapsed(0); return; }
    const started = new Date(active.started_at).getTime();
    const tick = () => setElapsed(Math.floor((Date.now() - started) / 1000));
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [active]);

  if (!active) return null;
  const h = Math.floor(elapsed / 3600), m = Math.floor((elapsed % 3600) / 60), s = elapsed % 60;

  const stop = async () => {
    try {
      await api.post(`/tasks/${active.task_id}/time/stop`);
      setActive(null);
      window.dispatchEvent(new CustomEvent("hreye:timer-changed"));
      toast.success("Timer stopped");
    } catch { toast.error("Failed"); }
  };

  return (
    <div className="h-9 bg-gradient-to-r from-cyan-500 to-sky-600 text-white px-4 flex items-center gap-3 justify-between text-xs shadow-sm" data-testid="focus-mode-bar">
      <div className="flex items-center gap-2 min-w-0">
        <span className="relative flex w-2 h-2">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-60"></span>
          <span className="relative inline-flex rounded-full h-2 w-2 bg-white"></span>
        </span>
        <span className="font-semibold">Focus mode · Timer running</span>
        <span className="opacity-80 truncate hidden sm:inline max-w-[240px]">· {active.title}</span>
      </div>
      <div className="flex items-center gap-2">
        <span className="font-bold tabular-nums" data-testid="focus-elapsed">{h > 0 ? `${h}:` : ""}{String(m).padStart(2, "0")}:{String(s).padStart(2, "0")}</span>
        <button onClick={() => nav(`/tasks?open=${active.task_id}`)} className="px-2 h-6 rounded-md bg-white/15 hover:bg-white/25 transition" data-testid="focus-view-task">View task</button>
        <button onClick={stop} className="px-2 h-6 rounded-md bg-white text-cyan-700 font-semibold hover:bg-white/95 transition" data-testid="focus-stop">Stop</button>
      </div>
    </div>
  );
}
